import IconCardOutline from '@assets/icons/IconCardOutline';
import IconShoppingBagOutline from '@assets/icons/IconShoppingBagOutline';
import IconHistoryOutline from '@assets/icons/IconHistoryOutline';
import IconSpeedOutline from '@assets/icons/IconSpeedOutline';
import IconPercentDiscountOutline from '@assets/icons/IconPercentDiscountOutline';
import IconSettingsOutline from '@assets/icons/IconSettingsOutline';
import IconLogoutOutline from '@assets/icons/IconLogoutOutline';
import { pushClickMenuGTMEvent } from '@utils/gtm';
import ROUTE_URLS from '@constants/routeUrls';
import { useTranslation } from 'next-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectUser } from '@store/auth/auth.slice';
import { useRouter } from 'next/router';
import Link from '@components/elements/Link';
import tw from 'twin.macro';
import useAppConfig from '@hooks/useAppConfig';
import useMediaQuery from '@hooks/useMediaQuery';
import { down } from '@utils/screens';

import MoneyboxIcon from './MoneyboxIcon';

type AuthNavItemsProps = {
  onLinkClick: () => void;
};

const AuthNavItems = (props: AuthNavItemsProps) => {
  const { onLinkClick } = props;

  const { t } = useTranslation();

  const dispatch = useDispatch();
  const router = useRouter();
  const { id: userId, moneyBox: balance = 0 } = useSelector(selectUser);
  const {
    data: {
      modules: {
        MoneyBox: { shortName },
        ConfigClientPanel: { allowClientSeeMoneyBox, allowClientSeeOwnId },
      },
      panelOptions: { showReferrerCode },
    },
  } = useAppConfig();

  const isDownLg = useMediaQuery(down('lg'));

  const authNavItems = [
    {
      id: 0,
      icon: IconShoppingBagOutline,
      dataCy: 'user-dropdown--my-orders',
      href: ROUTE_URLS.MY_ORDERS,
      label: t('$*components.header.nav.myOrders'),
      onClick: () => {
        pushClickMenuGTMEvent({
          text: t('$*components.header.nav.myOrders'),
          action: 'dropdown menu',
          level: 'sub1',
        });
      },
    },
    {
      id: 1,
      icon: IconHistoryOutline,
      dataCy: 'user-dropdown--history',
      href: ROUTE_URLS.HISTORY_ORDERS,
      label: t('$*components.header.nav.historyOrders'),
      onClick: () => {
        pushClickMenuGTMEvent({
          text: t('$*components.header.nav.historyOrders'),
          action: 'dropdown menu',
          level: 'sub1',
        });
      },
    },
    {
      id: 2,
      icon: MoneyboxIcon,
      dataCy: 'user-dropdown--moneybox',
      visible: allowClientSeeMoneyBox,
      href: ROUTE_URLS.MONEY_BOX,
      label: isDownLg
        ? t('$*components.header.myAccount.moneyboxWithBalance', {
            balance,
            unit: shortName || t('$*common.pointAbbreviation'),
          })
        : t('$*components.header.myAccount.moneybox'),
      onClick: () => {
        pushClickMenuGTMEvent({
          text: t('$*components.header.myAccount.moneybox'),
          action: 'dropdown menu',
          level: 'sub1',
        });
      },
    },
    {
      id: 3,
      icon: IconSpeedOutline,
      dataCy: 'user-dropdown--bmi-calculator',
      href: ROUTE_URLS.BMI_CALCULATOR,
      label: t('$*components.header.nav.myBmi'),
      onClick: () => {
        pushClickMenuGTMEvent({
          text: t('$*components.header.nav.myBmi'),
          action: 'dropdown menu',
          level: 'sub1',
        });
      },
    },
    {
      id: 4,
      icon: IconPercentDiscountOutline,
      dataCy: 'user-dropdown--share-program',
      href: ROUTE_URLS.SHARE_PROGRAM,
      label: t('$*components.header.nav.shareProgram'),
      visible: showReferrerCode,
      onClick: () => {
        pushClickMenuGTMEvent({
          text: t('$*components.header.nav.shareProgram'),
          action: 'dropdown menu',
          level: 'sub1',
        });
      },
    },
    {
      id: 5,
      icon: IconSettingsOutline,
      dataCy: 'user-dropdown--settings',
      href: ROUTE_URLS.ACCOUNT_SETTINGS,
      label: t('$*components.header.myAccount.settings'),
      onClick: () => {
        pushClickMenuGTMEvent({
          text: t('$*components.header.myAccount.settings'),
          action: 'dropdown menu',
          level: 'sub1',
        });
      },
    },
  ];

  return (
    <>
      {allowClientSeeOwnId && (
        <div
          tw="grid grid-cols-[16px auto] font-bold bg-grey-50 p-2.5 m-1.5 rounded text-grey-600"
          data-cy="AuthNavItems_div"
        >
          <IconCardOutline tw="place-self-center" />
          <span tw="ml-2 self-center leading-none">
            {t('$*components.header.myAccount.userId', { userId })}
          </span>
        </div>
      )}
      {authNavItems.map(({ id, icon: Icon, visible = true, ...navItem }) => {
        if (!visible) {
          return null;
        }

        return (
          <Link
            href={navItem.href}
            key={id}
            onClick={() => {
              navItem.onClick();
              onLinkClick();
            }}
            data-cy={navItem.dataCy}
            tw="grid grid-cols-[16px auto] p-4 text-inherit"
          >
            <Icon tw="place-self-center" />
            <span
              tw="ml-2 self-center leading-none"
              css={router.asPath === navItem.href ? tw`font-bold` : null}
            >
              {navItem.label}
            </span>
          </Link>
        );
      })}
      <button
        type="button"
        onClick={() => {
          pushClickMenuGTMEvent({
            text: t('$*components.header.button.logout'),
            action: 'dropdown menu',
            level: 'sub1',
          });
          dispatch(logout());
          onLinkClick();
        }}
        data-cy="user-dropdown--logout"
        tw="grid grid-cols-[16px auto] p-4 text-inherit hover:(text-primary-hover) w-full text-left border-t border-grey-200 transition-colors duration-200"
      >
        <IconLogoutOutline tw="place-self-center" />
        <span tw="ml-2 self-center leading-none">
          {t('$*components.header.button.logout')}
        </span>
      </button>
    </>
  );
};

export default AuthNavItems;
